import axios from 'lib/axios'
import {
  ASSET_MODEL,
  AssetBreakdown,
  AssetBreakdownBucket,
  AssetBreakdownDimension,
  AssetBreakdownWireResponse,
  AssetColumn,
  AssetCount,
  AssetEntityDetails,
  AssetEntityWireResponse,
  AssetInventoryType,
  AssetLookupPage,
  AssetLookupWireResponse,
  AssetManifestColumnWire,
  AssetModel,
  AssetModelManifestWire,
  AssetRecentlyAdded,
  AssetRecordsQuery,
  AssetRecordsResponse,
  AssetRecordsWireResponse,
  AssetStatistics,
  AssetStatisticsWireResponse,
  AssetTrendPoint,
  AssetQueryFilters,
  AssetTrendWireResponse,
  LAST_SEEN_DAYS,
  LastSeenWindow,
  TrendPeriod
} from 'models'
import { RECENCY_COLUMN, toAssetColumns } from 'ui/pages/asset-inventory/assetInventory.columns'
import { toAssetRow } from 'ui/pages/asset-inventory/utils/assetRows'
import { toAssetEntityDetails } from 'ui/pages/asset-inventory/utils/assetEntity'
import { bucketLabel } from 'ui/pages/asset-inventory/utils/assetBreakdown'
import { EMPTY_STATISTICS } from 'ui/pages/asset-inventory/assetInventory.constants'

const basePath = '/api/v4/asset_inventory'

const EMPTY_RECORDS: AssetRecordsResponse = {
  available: false,
  rows: [],
  totalResults: 0,
  hasNextPage: false,
  lastUpdatedAt: null,
  snapshotDate: null
}

/* The endpoint's own default; the server caps it at 100,000. */
const LOOKUP_PAGE_SIZE = 10000

/**
 * Aggregates move once a day, at the nightly build, so a repeat within the window is served from
 * memory. Nothing tells the client the snapshot changed; the TTL is what picks a newer one up.
 */
const SNAPSHOT_TTL_MS = 5 * 60 * 1000

/* Stable across responses, so re-clicking the card replaces its chip instead of stacking one. */
const RECENTLY_ADDED_FILTER_ID = 'recently-added'

const postRecords = async (
  assetType: AssetInventoryType,
  body: Record<string, unknown>
): Promise<AssetRecordsWireResponse> => {
  const { data } = await axios.post<AssetRecordsWireResponse>(
    `${basePath}/${ASSET_MODEL[assetType]}/records`,
    body
  )

  return data
}

/* Trend and breakdown take the window as `last_days`; "all" sends none. */
const windowBody = (lastSeen: LastSeenWindow): { last_days?: number } => {
  const days = LAST_SEEN_DAYS[lastSeen]

  return days === null ? {} : { last_days: days }
}

/* Records take the window as a predicate on the recency column, ANDed with any range already there. */
const withWindow = (queryFilter: AssetQueryFilters, column: string, lastSeen: LastSeenWindow): AssetQueryFilters => {
  const days = LAST_SEEN_DAYS[lastSeen]

  if (days === null) return queryFilter

  return { ...queryFilter, [column]: { ...queryFilter[column], last_days_: days } }
}

/**
 * A bar's filter arrives with the window it was counted under. The grid adds the live window
 * itself, so a stale copy must not outlive a Last Seen change while the chip is up.
 */
const withoutWindow = (queryFilter: AssetQueryFilters, column: string): AssetQueryFilters => {
  const own = queryFilter[column]

  if (own?.last_days_ === undefined) return queryFilter

  const rest = { ...own }
  delete rest.last_days_

  const next = { ...queryFilter, [column]: rest }

  if (!Object.keys(rest).length) delete next[column]

  return next
}

const requestBody = (query: AssetRecordsQuery): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    limit: query.limit ?? 100,
    page: query.page ?? 1
  }

  const queryFilter = withWindow(query.queryFilter ?? {}, RECENCY_COLUMN[query.assetType], query.lastSeen)

  if (Object.keys(queryFilter).length) body.query_filter = queryFilter

  /* An empty scope searches every column, so the term only goes out with the visible ones. */
  if (query.search && query.searchColumns?.length) {
    body.search = query.search
    body.search_columns = query.searchColumns
  }

  if (query.sort) body.sort = query.sort

  return body
}

/* One page of entities, plus the totals the grid header and the count card read. */
const getRecords = async (query: AssetRecordsQuery): Promise<AssetRecordsResponse> => {
  const wire = await postRecords(query.assetType, requestBody(query))

  if (!wire.available) return EMPTY_RECORDS

  const { columns } = query

  return {
    available: true,
    rows: wire.data.map((record) => toAssetRow(record, columns)),
    totalResults: wire.pagination?.total_results ?? 0,
    hasNextPage: wire.pagination?.next_page !== null,
    lastUpdatedAt: wire.meta?.last_updated ?? null,
    snapshotDate: wire.meta?.snapshot_date ?? null
  }
}

const columnsCache = new Map<AssetInventoryType, Promise<AssetColumn[]>>()

const fetchManifestColumns = async (model: AssetModel): Promise<AssetManifestColumnWire[]> => {
  const { data } = await axios.post<AssetModelManifestWire[]>('/api/v4/manifests/models/filter', {
    manifest: { slug: { any_: [model] } }
  })

  return (data ?? []).find((manifest) => manifest.slug === model)?.spec?.columns ?? []
}

/* One row is enough to tell which manifest columns the records endpoint actually serves. */
const fetchRowKeys = async (assetType: AssetInventoryType): Promise<string[] | null> => {
  const wire = await postRecords(assetType, { limit: 1, page: 1 })
  const row = wire.available ? wire.data?.[0] : undefined

  return row ? Object.keys(row) : null
}

/**
 * The tab's columns, from the model manifest. Held for the session — the manifest changes with a
 * deploy, not a nightly build. A failure is not cached, so the next tab visit asks again.
 */
const getColumns = (assetType: AssetInventoryType): Promise<AssetColumn[]> => {
  const cached = columnsCache.get(assetType)

  if (cached) return cached

  const pending = Promise.all([fetchManifestColumns(ASSET_MODEL[assetType]), fetchRowKeys(assetType)]).then(
    ([manifestColumns, rowKeys]) => toAssetColumns(assetType, manifestColumns, rowKeys)
  )

  columnsCache.set(assetType, pending)
  pending.catch(() => columnsCache.delete(assetType))

  return pending
}

const getTrend = async (
  assetType: AssetInventoryType,
  lastSeen: LastSeenWindow,
  period: TrendPeriod
): Promise<AssetTrendPoint[]> => {
  const { data } = await axios.post<AssetTrendWireResponse>(`${basePath}/${ASSET_MODEL[assetType]}/trend`, {
    ...windowBody(lastSeen),
    time_period: period
  })

  if (!data?.available) return []

  return (data.data ?? []).map((point) => ({
    date: point.date,
    value: point.count,
    estimated: point.pointState === 'estimated'
  }))
}

interface StatisticsCacheEntry {
  statistics: AssetStatistics
  fetchedAt: number
}

const statisticsCache = new Map<AssetModel, StatisticsCacheEntry>()

const WINDOW_BY_DAYS = new Map(
  (Object.entries(LAST_SEEN_DAYS) as [LastSeenWindow, number | null][]).map(([window, days]) => [days, window])
)

/**
 * Both statistics cards in one request, for every window at once — so the global control moves
 * without asking again, and the totals are the grid's own boundary rather than a count this side
 * composed. Each window's total equals `/records` `total_results` for the same window.
 *
 * Recently added is fixed at its own window and carries the predicate it was counted with, which
 * only holds with no Last Seen window; under any other the grid ANDs the window in and returns
 * fewer rows than the card showed.
 */
const getStatistics = async (assetType: AssetInventoryType): Promise<AssetStatistics> => {
  const model = ASSET_MODEL[assetType]
  const cached = statisticsCache.get(model)

  if (cached && Date.now() - cached.fetchedAt < SNAPSHOT_TTL_MS) return cached.statistics

  const { data } = await axios.post<AssetStatisticsWireResponse>(`${basePath}/${model}/statistics`)

  if (!data?.available) return EMPTY_STATISTICS

  const windows: Partial<Record<LastSeenWindow, AssetCount>> = {}

  for (const counts of data.windows ?? []) {
    const window = WINDOW_BY_DAYS.get(counts.days)

    if (!window) continue

    windows[window] = {
      total: counts.total,
      changeSinceLastMonth: counts.change_since_last_month
    }
  }

  const wire = data.recently_added
  const recentlyAdded: AssetRecentlyAdded = wire
    ? {
        count: wire.count,
        days: wire.days,
        filter: {
          id: RECENTLY_ADDED_FILTER_ID,
          label: 'Recently added',
          value: `last ${wire.days} days`,
          queryFilter: wire.query_filter
        }
      }
    : EMPTY_STATISTICS.recentlyAdded

  const statistics: AssetStatistics = {
    available: true,
    windows,
    recentlyAdded,
    lastUpdatedAt: data.meta?.last_updated ?? null
  }

  statisticsCache.set(model, { statistics, fetchedAt: Date.now() })

  return statistics
}

/**
 * The details pane. Unlike a grid row this carries every distinct value a column took and the
 * product instances that reported each, so the +N badge can open instead of just counting.
 */
const getEntity = async (
  assetType: AssetInventoryType,
  entityId: string
): Promise<AssetEntityDetails | null> => {
  const { data } = await axios.post<AssetEntityWireResponse>(
    `${basePath}/${ASSET_MODEL[assetType]}/records/${encodeURIComponent(entityId)}`,
    {}
  )

  if (!data?.available || !data.data) return null

  return toAssetEntityDetails(data.data)
}

const NO_LOOKUP: AssetLookupPage = { available: false, values: [], hasNoValue: false, hasMore: false }

/**
 * One page of the distinct values a column can be filtered on. Paging exists for first paint, not
 * to bound the list — the caller is expected to keep asking until `hasMore` is false so that the
 * client-side search in the dropdown sees every value.
 *
 * A 400 means the column has no pick list at all, which is an answer rather than a failure: the
 * dropdown falls back to matching on the term typed into it.
 */
const getLookupPage = async (
  assetType: AssetInventoryType,
  columnName: string,
  page = 1
): Promise<AssetLookupPage> => {
  const { data } = await axios
    .post<AssetLookupWireResponse>(`${basePath}/${ASSET_MODEL[assetType]}/lookup`, {
      column_name: columnName,
      page,
      limit: LOOKUP_PAGE_SIZE
    })
    .catch((error) => {
      if (error?.response?.status === 400) return { data: null }

      throw error
    })

  if (!data?.available) return NO_LOOKUP

  return {
    available: true,
    values: data.values ?? [],
    hasNoValue: !!data.has_no_value,
    hasMore: !!data.pagination?.has_more
  }
}

const EMPTY_BREAKDOWN: AssetBreakdown = { available: false, buckets: [], lastUpdatedAt: null }

interface BreakdownCacheEntry {
  breakdown: AssetBreakdown
  fetchedAt: number
}

/* A newer `last_updated` on any answer drops the rest of that model's entries with it. */
const breakdownCache = new Map<string, BreakdownCacheEntry>()

const breakdownKey = (model: AssetModel, dimension: string, lastSeen: LastSeenWindow) =>
  `${model}|${dimension}|${lastSeen}`

const dropStaleBreakdowns = (model: AssetModel, lastUpdatedAt: string | null) => {
  Array.from(breakdownCache.entries()).forEach(([key, entry]) => {
    if (key.startsWith(`${model}|`) && entry.breakdown.lastUpdatedAt !== lastUpdatedAt) breakdownCache.delete(key)
  })
}

/**
 * The tab's entities grouped by one column. Each bar carries the predicate that selects it, which
 * the grid sends on with the window on screen, so the row count matches the bar the user clicked.
 */
const getBreakdown = async (
  assetType: AssetInventoryType,
  lastSeen: LastSeenWindow,
  dimension: AssetBreakdownDimension
): Promise<AssetBreakdown> => {
  const model = ASSET_MODEL[assetType]
  const key = breakdownKey(model, dimension.column, lastSeen)
  const cached = breakdownCache.get(key)

  if (cached && Date.now() - cached.fetchedAt < SNAPSHOT_TTL_MS) return cached.breakdown

  const { data } = await axios.post<AssetBreakdownWireResponse>(`${basePath}/${model}/breakdown`, {
    dimension: dimension.column,
    ...windowBody(lastSeen)
  })

  if (!data?.available) return EMPTY_BREAKDOWN

  const lastUpdatedAt = data.meta?.last_updated ?? null

  const buckets: AssetBreakdownBucket[] = (data.data ?? []).map((bucket) => ({
    label: bucket.label,
    value: bucket.count,
    kind: bucket.kind,
    filter: {
      id: dimension.column,
      label: dimension.label,
      value: bucketLabel(bucket.label, bucket.kind),
      queryFilter: withoutWindow(bucket.query_filter, RECENCY_COLUMN[assetType])
    }
  }))

  dropStaleBreakdowns(model, lastUpdatedAt)

  const breakdown: AssetBreakdown = { available: true, buckets, lastUpdatedAt }
  breakdownCache.set(key, { breakdown, fetchedAt: Date.now() })

  return breakdown
}

export const AssetInventoryServiceV4 = {
  getColumns,
  getRecords,
  getEntity,
  getLookupPage,
  getTrend,
  getStatistics,
  getBreakdown
}
